import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "sonner";

import {
  getBillingPlansService,
  postAddonPreviewService,
  postAddonUpdateService,
  getBillingStatusService,
  getCheckoutStatusService,
  postCheckoutService,
  postContactInquiryService,
  postPortalService,
} from "@/services/billing.service";
import { isApiErrorHandled } from "@/services/api";
import { IAPIResponse } from "@/services/api.types";
import {
  AddonPreviewFormData,
  AddonPreviewResponse,
  AddonUpdateFormData,
  AddonUpdateResponse,
  BillingPlansResponse,
  BillingStatusResponse,
  CheckoutFormData,
  CheckoutResponse,
  CheckoutStatusResponse,
  ContactInquiryFormData,
  ContactInquiryResponse,
  PortalFormData,
  PortalResponse,
} from "@/types/billing.types";

export const billingQueryKeys = {
  all: () => ["billing"] as const,
  plans: () => [...billingQueryKeys.all(), "plans"] as const,
  status: () => [...billingQueryKeys.all(), "status"] as const,
  checkoutStatus: (sessionId: string) => [...billingQueryKeys.all(), "checkout-status", sessionId] as const,
};

export const useGetBillingPlans = () => {
  return useQuery<BillingPlansResponse, AxiosError>({
    queryKey: billingQueryKeys.plans(),
    queryFn: getBillingPlansService,
  });
};

export const usePostContactInquiry = () => {
  return useMutation<IAPIResponse<ContactInquiryResponse>, Error, ContactInquiryFormData>({
    mutationFn: postContactInquiryService,
    retry: false,
    onSuccess: res => {
      toast.success(res.message);
    },
    onError: err => {
      toast.error(err.message);
    },
  });
};

/**
 * Opens Stripe's hosted customer portal.
 *
 * Changing plan, updating a card and cancelling all live there rather than
 * in-app: Stripe keeps those flows compliant and current, and every action a
 * user takes emits the subscription webhooks the backend already handles.
 */
export const usePostPortal = () => {
  return useMutation<IAPIResponse<PortalResponse>, Error, PortalFormData>({
    mutationFn: postPortalService,
    onSuccess: res => {
      if (res.data?.url) window.location.href = res.data.url;
    },
    onError: err => toast.error(err.message),
  });
};

export const useGetBillingStatus = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery<BillingStatusResponse, AxiosError>({
    queryKey: billingQueryKeys.status(),
    queryFn: getBillingStatusService,
    enabled,
  });
};

export const usePostCheckout = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<CheckoutResponse>, Error, CheckoutFormData>({
    mutationFn: postCheckoutService,
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingQueryKeys.status() });
    },
    onError: err => {
      toast.error(err.message);
    },
  });
};

/**
 * Asks Stripe what a change would cost, before anything is charged.
 *
 * A mutation rather than a query because it POSTs, but it is read-only — an
 * invoice preview creates nothing. Deliberately silent on error: a preview that
 * fails must not stop someone buying, so the dialog falls back to its unquoted
 * wording instead of surfacing a toast about a number it could not fetch.
 */
export const usePreviewAddon = () => {
  return useMutation<IAPIResponse<AddonPreviewResponse>, Error, AddonPreviewFormData>({
    mutationFn: postAddonPreviewService,
    retry: false,
  });
};

/**
 * Changes one add-on's quantity on an existing subscription.
 *
 * Increases are charged immediately and prorated; reductions take effect at the
 * end of the period already paid for. Both outcomes are reported by the server,
 * so the toast says which one happened rather than guessing from the sign.
 *
 * A prorated charge can need SCA, in which case Stripe leaves the invoice
 * unpaid and the server hands back `hosted_invoice_url`. Reporting success
 * there would tell someone their limit went up when the money never moved —
 * so that case redirects instead.
 */
export const useUpdateAddon = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<AddonUpdateResponse>, Error, AddonUpdateFormData>({
    mutationFn: postAddonUpdateService,
    retry: false,
    onSuccess: res => {
      const data = res.data;
      if (data?.requires_action && data.hosted_invoice_url) {
        toast.info("One more step — your bank needs to confirm this payment.");
        window.location.href = data.hosted_invoice_url;
        return;
      }
      queryClient.invalidateQueries({ queryKey: billingQueryKeys.status() });
      if (data?.unchanged) return;
      toast.success(
        data?.effective === "period_end"
          ? "Change saved — it takes effect at your next renewal."
          : "Updated. Your new limit is live."
      );
    },
    // The axios interceptor already toasts the billing-shaped failures (402,
    // and the plan-limit 409s) with their own action buttons. Toasting again
    // here would stack two notifications for one refusal.
    onError: err => {
      if (!isApiErrorHandled(err)) toast.error(err.message);
    },
  });
};

export const useGetCheckoutStatus = (sessionId: string, { enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery<CheckoutStatusResponse, AxiosError>({
    queryKey: billingQueryKeys.checkoutStatus(sessionId),
    queryFn: () => getCheckoutStatusService(sessionId),
    enabled: enabled && !!sessionId,
  });
};
